import React from "react";
import styled from "@emotion/styled";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import MenuItem from "./optioncomponents/MenuItem";
import { ThemeContext, UserContext } from "../contexts/Context";

const MenuRecommendationItems = ({
  recommendation,
  width,
  sidebarWidth,
  theme,
  selectedItems,
  addItemDirect,
  cart,
  removeFromCart,
  setSelectedMenu,
  menuSelectionFormRef,
  catPos,
  setLastPos
}) => {
  const themeContext = React.useContext(ThemeContext);
  const { lanIndex, currencySymbol, isLanReverse, displayMenuDescription } = React.useContext(UserContext);
  const recommendationListRef = React.useRef(null);
  const [scrollState, setScrollState] = React.useState({ canScrollLeft: false, canScrollRight: false });
  const { isDesktop, itemWidth, mobileCardInset } = React.useMemo(() => {
    const availableWidth = Math.max(0, width - sidebarWidth);
    const desktop = width >= 768;
    const mobileInset = Number(catPos) === 0 ? 14 : 18;
    const desktopMenuCardWidth = (availableWidth / 3) - Math.max(34, Math.min(50, availableWidth * 0.025 + 16));

    return {
      isDesktop: desktop,
      itemWidth: desktop
        ? Math.max(150, Math.min(340, Math.round(desktopMenuCardWidth * 0.84)))
        : Math.max(136, Math.min(170, Math.round(availableWidth * 0.44))),
      mobileCardInset: mobileInset
    };
  }, [catPos, sidebarWidth, width]);

  const updateScrollState = React.useCallback(() => {
    const list = recommendationListRef.current;
    if (!list) return;
    const maxScrollLeft = Math.max(0, list.scrollWidth - list.clientWidth);
    setScrollState({
      canScrollLeft: list.scrollLeft > 2,
      canScrollRight: list.scrollLeft < maxScrollLeft - 2
    });
  }, []);

  React.useEffect(() => {
    const list = recommendationListRef.current;
    if (!list) return undefined;

    updateScrollState();
    list.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);

    return () => {
      list.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [recommendation.options.length, itemWidth, updateScrollState]);

  const showDescription = displayMenuDescription === true || displayMenuDescription === "true";

  const scrollRecommendations = (direction) => {
    const list = recommendationListRef.current;
    if (!list) return;
    list.scrollBy({
      left: direction * (itemWidth - (isDesktop ? 16 : mobileCardInset) + 8) * 2,
      behavior: "smooth"
    });
  };

  const selectItem = (item) => {
    const selectedItem = JSON.parse(JSON.stringify(item));
    selectedItem.optionArray = Array.isArray(selectedItem.optionArray) ? selectedItem.optionArray : [];
    selectedItem.recommendedArray = Array.isArray(selectedItem.recommendedArray) ? selectedItem.recommendedArray : [];
    selectedItem.qty = 1;
    selectedItem.recommendId = "MREC";
    menuSelectionFormRef.current = JSON.parse(JSON.stringify(selectedItem));
    setSelectedMenu(selectedItem);

    if (!addItemDirect || selectedItem.optionArray.length > 0) {
      setLastPos(1);
    }
  };

  const removeItem = (event, item) => {
    event.stopPropagation();
    const newCart = JSON.parse(JSON.stringify(cart));
    for (let index = newCart.length - 1; index >= 0; index--) {
      if (newCart[index].codeId === item.codeId) {
        newCart[index].qty--;
        const minQty = newCart[index].minQty ? newCart[index].minQty - 1 : 0;
        if (newCart[index].qty <= minQty) newCart.splice(index, 1);
        localStorage.setItem("order", JSON.stringify(newCart));
        removeFromCart(newCart, index);
        break;
      }
    }
  };

  return (
    <RecommendationContainer themeContext={themeContext}>
      <RecommendationTitle themeContext={themeContext}>
        {recommendation.optionTitle || "Add-on"}
      </RecommendationTitle>
      <RecommendationRail>
        <ScrollButton
          type="button"
          side="left"
          themeContext={themeContext}
          disabled={!scrollState.canScrollLeft}
          onClick={() => scrollRecommendations(-1)}
          aria-label="Previous recommended items"
        >
          <IoIosArrowBack size={24} />
        </ScrollButton>
        <RecommendationList ref={recommendationListRef}>
          {recommendation.options.map((item, index) => {
            const displayItem = { ...item, size: 0 };
            const cardWidth = itemWidth - (isDesktop ? 16 : mobileCardInset);
            const cardHeight = cardWidth + (isDesktop
              ? (showDescription ? 164 : 120)
              : (showDescription ? 178 : 136));
            return (
              <RecommendationItem key={item.codeId || index} width={cardWidth}>
                <MenuItem
                  item={displayItem}
                  containerWidth={cardWidth}
                  containerHeight={cardHeight}
                  handleOnClick={() => selectItem(item)}
                  addQty={() => selectItem(item)}
                  minusQty={(event) => removeItem(event, item)}
                  theme={theme}
                  lanIndex={lanIndex}
                  isLanReverse={isLanReverse}
                  selectedItems={selectedItems}
                  currencySymbol={currencySymbol}
                  addItemDirect={addItemDirect}
                  cp={catPos}
                  menuListCard
                  recommendationCard
                />
              </RecommendationItem>
            );
          })}
        </RecommendationList>
        <ScrollButton
          type="button"
          side="right"
          themeContext={themeContext}
          disabled={!scrollState.canScrollRight}
          onClick={() => scrollRecommendations(1)}
          aria-label="Next recommended items"
        >
          <IoIosArrowForward size={24} />
        </ScrollButton>
      </RecommendationRail>
    </RecommendationContainer>
  );
};

export default React.memo(MenuRecommendationItems);

const RecommendationContainer = styled.section`
  display: block;
  width: 100%;
  padding-top: 18px;
  margin-top: 10px;
  border-bottom: 1px solid ${(props) => props.themeContext.borderColor || "rgba(0, 0, 0, 0.08)"};
`;

const RecommendationTitle = styled.div`
  margin: 0 18px 12px;
  color: ${(props) => props.themeContext.fontPrimary};
  font-family: ${(props) => props.themeContext.fontFamily};
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
  text-align: left;
`;

const RecommendationRail = styled.div`
  position: relative;
  width: 100%;
`;

const RecommendationList = styled.div`
  display: flex;
  width: 100%;
  gap: 8px;
  padding: 0 10px 20px;
  box-sizing: border-box;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const RecommendationItem = styled.div`
  flex: 0 0 ${(props) => props.width}px;
  width: ${(props) => props.width}px;
  display: flex;
  align-items: flex-start;

  > div {
    margin-left: 0;
    margin-right: 0;
    flex-shrink: 0;
  }
`;

const ScrollButton = styled.button`
  display: none;

  @media (min-width: 768px) {
    position: absolute;
    top: 50%;
    ${(props) => props.side}: 12px;
    z-index: 4;
    display: flex;
    width: 42px;
    height: 42px;
    padding: 0;
    align-items: center;
    justify-content: center;
    border: 1px solid ${(props) => props.themeContext.borderColor || "rgba(0, 0, 0, 0.12)"};
    border-radius: 50%;
    color: ${(props) => props.themeContext.fontPrimary};
    background: ${(props) => props.themeContext.backgroundColor};
    box-shadow: 0 5px 18px rgba(0, 0, 0, 0.2);
    transform: translateY(-50%);
    cursor: pointer;
    transition: opacity 160ms ease, transform 160ms ease, box-shadow 160ms ease;

    &:hover:not(:disabled) {
      transform: translateY(-50%) scale(1.06);
      box-shadow: 0 8px 22px rgba(0, 0, 0, 0.24);
    }

    &:disabled {
      opacity: 0;
      pointer-events: none;
    }
  }
`;
